import { useEffect, useRef, useState } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Form, FormField } from 'core/components';
import { FormError } from 'core/elements';
import { InputType } from 'core/enums';
import { useFormContext, useNotificationContext } from 'core/states';
import { useCreateProspectQuote, useGetProspectContactInfo } from 'quotes/hooks';
import { quotePartsPath } from 'quotes/routes/routePaths';
import { ICreateProspectQuoteResponse, IProspectQuoteForm } from 'quotes/types';

import { mapProspectQuoteForm } from './mappers';
import { isValidEmail } from './utils';

const LOOKUP_DEBOUNCE_MS = 500;
const CLOSE_DELAY_MS = 1000;

interface IProps {
  defaultValues: IProspectQuoteForm;
  isOpen: boolean;
  onClose: () => void;
}

export const ProspectQuoteUpsertFormFields = ({ defaultValues, isOpen, onClose }: IProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { clearNotification } = useNotificationContext();
  const { isSubmitting, setValue, watch } = useFormContext();
  const { createProspectQuote } = useCreateProspectQuote();

  const [lookupEmail, setLookupEmail] = useState('');

  const email = watch('email');
  const trimmedEmail = (email ?? '').trim();

  const prefilledCompanyRef = useRef<string | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current != null) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    if (!isValidEmail(trimmedEmail)) {
      setLookupEmail('');

      return;
    }

    const timeoutId = setTimeout(() => setLookupEmail(trimmedEmail), LOOKUP_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [trimmedEmail]);

  const { contactInfo, isError, isFetching } = useGetProspectContactInfo(
    lookupEmail,
    isOpen && Boolean(lookupEmail)
  );

  // The lookup verdict is keyed on the debounced email; while the field runs ahead of it, the
  // previous verdict no longer describes what was typed, so editing the email drops it.
  // Kept out of RHF errors on purpose: Form re-runs its errors effect on every errors change,
  // and that effect focuses + smooth-scrolls the first errored field, stealing focus mid-typing.
  const isLookupCurrent = trimmedEmail === lookupEmail && !isFetching;
  const isLookupPending = isValidEmail(trimmedEmail) && !isLookupCurrent;

  const existingContact = isLookupCurrent ? contactInfo?.contact ?? null : null;
  // A failed lookup is UNKNOWN, not "no match" — block submit instead of silently skipping
  // the duplicate check.
  const hasLookupFailed = isLookupCurrent && isError;
  const matchedCompanyName = contactInfo?.contactGroup?.name ?? null;

  useEffect(() => {
    return () => clearCloseTimeout();
  }, []);

  // A matched company wins over the typed name server-side, so mirror it into the (disabled)
  // input; dropping back to no match must not leave the previous match's name behind.
  useEffect(() => {
    if (matchedCompanyName) {
      prefilledCompanyRef.current = matchedCompanyName;

      setValue('companyName', matchedCompanyName, { shouldValidate: true });

      return;
    }

    if (prefilledCompanyRef.current !== null) {
      prefilledCompanyRef.current = null;

      setValue('companyName', '');
    }
  }, [matchedCompanyName, setValue]);

  const handleOnClose = (reset?: UseFormReset<IProspectQuoteForm>) => {
    clearCloseTimeout();
    clearNotification();
    setLookupEmail('');
    prefilledCompanyRef.current = null;
    onClose();
    reset?.(defaultValues);
  };

  const onSubmit =
    (reset: UseFormReset<IProspectQuoteForm>) => async (data: IProspectQuoteForm) => {
      let response: ICreateProspectQuoteResponse | undefined;

      try {
        response = await createProspectQuote(mapProspectQuoteForm(data));
      } catch {
        // The endpoint answers with real 4xx statuses, so the mutation rejects; useHandleErrors
        // has already surfaced the server message.
        return false;
      }

      const quoteId = response?.data?.quote?.id;

      if (!response?.success || !quoteId) {
        return false;
      }

      clearCloseTimeout();

      closeTimeoutRef.current = setTimeout(() => {
        handleOnClose(reset);
        navigate(quotePartsPath(quoteId));
      }, CLOSE_DELAY_MS);
    };

  const emailHelperTexts = existingContact
    ? [<FormError key="contactExists" error={t('errorProspectQuoteContactExists')} />]
    : hasLookupFailed
    ? [t('messageProspectQuoteEmailNotVerified')]
    : [];

  return (
    <Form
      title={t('titleProspectQuote')}
      isOpen={isOpen}
      onClose={handleOnClose}
      onSubmit={onSubmit}
      submitBtnLabel={t('buttonLabelCreate')}
      cancelBtnLabel={t('buttonLabelCancel')}
      disabledSubmit={
        isSubmitting ||
        isLookupPending ||
        hasLookupFailed ||
        Boolean(existingContact) ||
        // The close timer is armed before RHF flips isSubmitting back to false, so the
        // re-render that re-enables the button already sees the ref set — without this the
        // form stays clickable for CLOSE_DELAY_MS and a second click creates a second quote.
        Boolean(closeTimeoutRef.current)
      }
    >
      <FormField
        name="firstName"
        type={InputType.TEXT_INPUT}
        inputProps={{ label: t('labelFirstName'), required: true }}
      />

      <FormField
        name="lastName"
        type={InputType.TEXT_INPUT}
        inputProps={{ label: t('labelLastName'), required: true }}
      />

      <FormField
        name="email"
        type={InputType.TEXT_INPUT}
        inputProps={{
          helperTexts: emailHelperTexts,
          label: t('labelEmail'),
          required: true,
          showFieldError: true,
        }}
      />

      <FormField
        name="companyName"
        type={InputType.TEXT_INPUT}
        inputProps={{
          disabled: Boolean(matchedCompanyName),
          helperTexts: matchedCompanyName ? [t('messageProspectQuoteCompanyMatched')] : [],
          label: t('labelCompanyName'),
          required: true,
        }}
      />
    </Form>
  );
};
